import { View, Text, Svg, Path, Circle } from '@react-pdf/renderer';
import { shared } from './styles';
import { OdontogramPdf } from './OdontogramPdf';
import { ELASTIC_BUTTON, ELASTIC_BUTTON_LINGUAL, placementColor } from '../clinical/elastics/model';
import type { ButtonShape, ElasticScheme, LegacyElastics } from '../clinical/elastics/model';
import {
    elasticSchemeName,
    elasticSchemeRangeLabel,
    schemesHaveElastics,
    toothWithSide
} from '../clinical/elastics/persistence';

// Helvetica (the default PDF font) has no "→" glyph, so the tooth sequence uses
// an ASCII arrow.
const ARROW = '->';
/** Horizontal gap between grid columns and vertical gap between rows (pt). */
const GRID_GAP = 6;

/** The swatch mirrors the diagram (round buccal, triangular lingual) so the
 *  shape reads the same on a B&W printout. */
function ElasticsLegendPdf() {
    const swatch = (fill: string, stroke: string, shape: ButtonShape) => (
        <Svg width={8} height={8} viewBox="0 0 8 8">
            {shape === 'circle' ? (
                <Circle cx={4} cy={4} r={3} fill={fill} stroke={stroke} strokeWidth={1} />
            ) : (
                <Path d="M4 0.5 L7.5 7 L0.5 7 Z" fill={fill} stroke={stroke} strokeWidth={1} />
            )}
        </Svg>
    );
    const item = (fill: string, stroke: string, side: string, shape: ButtonShape) => (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
            {swatch(fill, stroke, shape)}
            <Text style={{ fontSize: 7, color: '#6b7280' }}>{side}</Text>
        </View>
    );
    return (
        <View
            style={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 8,
                marginTop: 3
            }}
        >
            <Text style={{ fontSize: 7, color: '#9ca3af' }}>Button, hook, cut-outs on:</Text>
            {item(ELASTIC_BUTTON.fill, ELASTIC_BUTTON.stroke, 'Buccal', ELASTIC_BUTTON.shape)}
            {item(
                ELASTIC_BUTTON_LINGUAL.fill,
                ELASTIC_BUTTON_LINGUAL.stroke,
                'Lingual',
                ELASTIC_BUTTON_LINGUAL.shape
            )}
        </View>
    );
}

interface ElasticsPdfSectionProps {
    schemes: ElasticScheme[];
    /** Legacy per-side elastics from a pre-odontogram event; rendered as the old
     *  Left/Right layout when there are no schemes. */
    legacy?: LegacyElastics;
    /** Rendered width of the odontogram diagram in PDF points. */
    width?: number;
}

/** Numbering is install order; pre-odontogram events fall back to the legacy
 *  Left/Right layout since they carry per-side strings instead of schemes. */
export function ElasticsPdfSection({ schemes, legacy, width = 420 }: ElasticsPdfSectionProps) {
    if (!schemesHaveElastics(schemes)) {
        if (legacy) return <LegacyElasticsPdf legacy={legacy} />;
        return (
            <View style={{ marginTop: 6 }}>
                <Text style={shared.elasticValueText}>No elastics placed.</Text>
            </View>
        );
    }
    return (
        <View style={{ marginTop: 6 }}>
            {schemes
                .filter((scheme) => scheme.elastics.length > 0)
                .map((scheme, index) => (
                    <SchemePdf
                        key={scheme.id}
                        scheme={scheme}
                        width={width}
                        showDivider={index > 0}
                    />
                ))}
        </View>
    );
}

/** `showDivider` draws a light rule separating the block from the previous scheme. */
function SchemePdf({
    scheme,
    width,
    showDivider
}: {
    scheme: ElasticScheme;
    width: number;
    showDivider: boolean;
}) {
    const placements = scheme.elastics;
    const name = elasticSchemeName(scheme);
    const range = elasticSchemeRangeLabel(scheme);
    // When the name still equals the range (auto default), don't print it twice.
    const heading = name !== range ? `${name} · ${range}` : range;
    return (
        // wrap={false} keeps the header, diagram and list of one scheme on the same
        // page instead of splitting the block across a page break.
        <View
            wrap={false}
            style={{
                marginBottom: 14,
                width: '100%',
                ...(showDivider
                    ? { borderTopWidth: 1, borderTopColor: '#e5e7eb', paddingTop: 14 }
                    : {})
            }}
        >
            <Text
                style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: '#374151',
                    marginBottom: 4
                }}
            >
                {heading}
            </Text>
            <View style={{ alignItems: 'center' }}>
                <OdontogramPdf placements={placements} width={width} />
            </View>

            <ElasticsLegendPdf />

            {/* Placed-elastics list — mirrors the on-screen review panel */}
            <View style={{ marginTop: 10, width: '100%' }}>
                <Text
                    style={{
                        fontSize: 8,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        color: '#9ca3af',
                        marginBottom: 4
                    }}
                >
                    PLACED ELASTICS
                </Text>
                <Text style={{ fontSize: 8, color: '#9ca3af', marginBottom: 6 }}>
                    Numbered by install order · red circle = buccal (B), blue triangle = lingual (L)
                    · arrow = direction.
                </Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: GRID_GAP }}>
                    {placements.map((placement, index) => {
                        const color = placementColor(placement);
                        const name = placement.name.trim() || `Elastic ${index + 1}`;
                        const teeth = placement.teeth;
                        const rest =
                            teeth.length > 1
                                ? ` ${ARROW} ${teeth
                                      .slice(1)
                                      .map((f) => toothWithSide(placement, f))
                                      .join(` ${ARROW} `)}`
                                : '';
                        return (
                            <View
                                key={placement.id}
                                style={{
                                    width: '48%',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 8,
                                    borderWidth: 1,
                                    borderColor: '#e5e7eb',
                                    borderRadius: 8,
                                    backgroundColor: '#ffffff',
                                    paddingVertical: 6,
                                    paddingHorizontal: 10
                                }}
                            >
                                <View
                                    style={{
                                        width: 18,
                                        height: 18,
                                        borderRadius: 9,
                                        backgroundColor: color,
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Text
                                        style={{ fontSize: 9, fontWeight: 700, color: '#ffffff' }}
                                    >
                                        {index + 1}
                                    </Text>
                                </View>
                                <View style={{ flexGrow: 1, flexShrink: 1 }}>
                                    <Text
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 500,
                                            color: '#1f2937'
                                        }}
                                    >
                                        {name}
                                    </Text>
                                    <Text style={{ fontSize: 9, color: '#6b7280', marginTop: 1 }}>
                                        <Text style={{ fontWeight: 700, color: '#374151' }}>
                                            {toothWithSide(placement, teeth[0])}
                                        </Text>
                                        {rest}
                                    </Text>
                                </View>
                                <View style={{ alignItems: 'flex-end' }}>
                                    <Text style={{ fontSize: 9, color: '#6b7280' }}>
                                        {placement.size || '-'}
                                    </Text>
                                    {placement.wear ? (
                                        <Text
                                            style={{ fontSize: 8, color: '#9ca3af', marginTop: 1 }}
                                        >
                                            {placement.wear}
                                        </Text>
                                    ) : null}
                                </View>
                            </View>
                        );
                    })}
                </View>
            </View>

            {/* Per-scheme elastic-tension guidance, repeated behind each scheme's diagram. */}
            <View
                style={{
                    marginTop: 10,
                    borderTopWidth: 1,
                    borderTopColor: '#e5e7eb',
                    paddingTop: 8
                }}
            >
                <Text style={shared.warningText}>
                    If 3/16&quot; 6.5oz cannot be tolerated by the patient (too tight), then retry
                    the following sizes in order: 3/16 4.5oz; 5/16 6.5oz; 5/16 4.5oz. The goal is to
                    use as much tension as possible for best results. After 4 aligners attempt to go
                    back to the tighter elastic. For CIII cross-bite, use 5/16 6.5oz elastics (5/16
                    4.5oz if it is too tight).
                </Text>
            </View>
        </View>
    );
}

/** Legacy Left/Right elastics layout for pre-odontogram events. */
function LegacyElasticsPdf({ legacy }: { legacy: LegacyElastics }) {
    return (
        <View style={shared.elasticsRow}>
            <View style={shared.elasticColumn}>
                <View style={shared.elasticField}>
                    <Text style={shared.elasticLabel}>Left elastics</Text>
                    <View style={shared.elasticValue}>
                        <Text style={shared.elasticValueText}>{legacy.leftElastic || 'N/A'}</Text>
                    </View>
                </View>
                <View style={shared.elasticField}>
                    <Text style={shared.elasticLabel}>Size</Text>
                    <View style={shared.elasticValue}>
                        <Text style={shared.elasticValueText}>
                            {legacy.leftElasticSize || 'N/A'}
                        </Text>
                    </View>
                </View>
            </View>
            <View style={shared.elasticColumn}>
                <View style={shared.elasticField}>
                    <Text style={shared.elasticLabel}>Right elastics</Text>
                    <View style={shared.elasticValue}>
                        <Text style={shared.elasticValueText}>{legacy.rightElastic || 'N/A'}</Text>
                    </View>
                </View>
                <View style={shared.elasticField}>
                    <Text style={shared.elasticLabel}>Size</Text>
                    <View style={shared.elasticValue}>
                        <Text style={shared.elasticValueText}>
                            {legacy.rightElasticSize || 'N/A'}
                        </Text>
                    </View>
                </View>
            </View>
        </View>
    );
}
